import React, { useState, useEffect } from 'react';
import { AppLayout } from '../components/AppLayout';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { CheckCircle2, XCircle, Clock, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';
import { formatDistanceToNow } from 'date-fns';
import { it } from 'date-fns/locale';

const STATUS_CONFIG = {
  pending: {
    label: 'In Attesa',
    className: 'bg-amber-50 text-amber-900 border-amber-200',
    icon: Clock,
  },
  approved: {
    label: 'Approvato',
    className: 'bg-emerald-50 text-emerald-900 border-emerald-200',
    icon: CheckCircle2,
  },
  rejected: {
    label: 'Rifiutato',
    className: 'bg-rose-50 text-rose-900 border-rose-200',
    icon: XCircle,
  },
};

const ROLE_LABELS = {
  admin: 'Admin',
  segnalatore: 'Segnalatore',
  ufficio_tecnico: 'Ufficio Tecnico',
  cliente: 'Cliente',
};

export default function AdminUsersPage() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);

  useEffect(() => {
    loadUsers();
  }, [selectedStatus]);

  const loadUsers = async () => {
    try {
      setLoading(true);
      const params = selectedStatus !== 'all' ? { status: selectedStatus } : {};
      const response = await api.get('/api/admin/users', { params });
      setUsers(response.data.users);
    } catch (error) {
      console.error('Failed to load users:', error);
      toast.error('Errore nel caricamento utenti');
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (userId) => {
    try {
      await api.post(`/api/admin/users/${userId}/approve`);
      toast.success('Utente approvato');
      loadUsers();
    } catch (error) {
      console.error('Failed to approve user:', error);
      toast.error(error.response?.data?.detail || 'Errore');
    }
  };

  const handleReject = async (userId) => {
    try {
      await api.post(`/api/admin/users/${userId}/reject`);
      toast.success('Utente rifiutato');
      loadUsers();
    } catch (error) {
      console.error('Failed to reject user:', error);
      toast.error(error.response?.data?.detail || 'Errore');
    }
  };

  const handleDeleteClick = (user) => {
    setUserToDelete(user);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!userToDelete) return;
    
    try {
      await api.delete(`/api/admin/users/${userToDelete.id}`);
      toast.success('Utente eliminato');
      setDeleteDialogOpen(false);
      setUserToDelete(null);
      loadUsers();
    } catch (error) {
      console.error('Failed to delete user:', error);
      toast.error(error.response?.data?.detail || 'Errore nell\'eliminazione');
    }
  };

  const handleRoleChange = async (userId, newRole) => {
    try {
      await api.put(`/api/admin/users/${userId}/role`, { role: newRole });
      toast.success('Ruolo aggiornato');
      loadUsers();
    } catch (error) {
      console.error('Failed to update role:', error);
      toast.error(error.response?.data?.detail || 'Errore nell\'aggiornamento');
    }
  };

  const pendingCount = users.filter((u) => u.status === 'pending').length;

  return (
    <AppLayout>
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-semibold tracking-tight">Gestione Utenti</h1>
          <p className="text-muted-foreground">Approva o rifiuta nuove registrazioni</p>
        </div>

        {/* Stats */}
        <div className="grid gap-4 md:grid-cols-3">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                In Attesa Approvazione
              </CardTitle>
              <Clock className="h-4 w-4 text-amber-500" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-semibold tabular-nums">{pendingCount}</div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                Utenti Approvati
              </CardTitle>
              <CheckCircle2 className="h-4 w-4 text-emerald-500" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-semibold tabular-nums">
                {users.filter((u) => u.status === 'approved').length}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                Totale Utenti
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-semibold tabular-nums">{users.length}</div>
            </CardContent>
          </Card>
        </div>

        {/* Users Table */}
        <Card>
          <CardHeader>
            <CardTitle>Utenti Registrati</CardTitle>
          </CardHeader>
          <CardContent>
            <Tabs value={selectedStatus} onValueChange={setSelectedStatus}>
              <TabsList>
                <TabsTrigger value="all">Tutti</TabsTrigger>
                <TabsTrigger value="pending">
                  In Attesa {pendingCount > 0 && `(${pendingCount})`}
                </TabsTrigger>
                <TabsTrigger value="approved">Approvati</TabsTrigger>
                <TabsTrigger value="rejected">Rifiutati</TabsTrigger>
              </TabsList>

              <div className="mt-4">
                {loading ? (
                  <div className="text-center py-8 text-muted-foreground">Caricamento...</div>
                ) : users.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    Nessun utente trovato
                  </div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Nome</TableHead>
                        <TableHead>Email</TableHead>
                        <TableHead>Ruolo</TableHead>
                        <TableHead>Stato</TableHead>
                        <TableHead>Registrato</TableHead>
                        <TableHead>Azioni</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {users.map((user) => {
                        const statusConfig = STATUS_CONFIG[user.status];
                        const StatusIcon = statusConfig?.icon;

                        return (
                          <TableRow key={user.id}>
                            <TableCell className="font-medium">{user.full_name}</TableCell>
                            <TableCell>{user.email}</TableCell>
                            <TableCell>
                              <Select
                                value={user.role}
                                onValueChange={(newRole) => handleRoleChange(user.id, newRole)}
                              >
                                <SelectTrigger className="w-[180px]">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="segnalatore">Segnalatore</SelectItem>
                                  <SelectItem value="ufficio_tecnico">Ufficio Tecnico</SelectItem>
                                  <SelectItem value="admin">Admin</SelectItem>
                                  <SelectItem value="cliente">Cliente</SelectItem>
                                </SelectContent>
                              </Select>
                            </TableCell>
                            <TableCell>
                              <Badge className={statusConfig?.className}>
                                {StatusIcon && <StatusIcon className="h-3.5 w-3.5 mr-1" />}
                                {statusConfig?.label}
                              </Badge>
                            </TableCell>
                            <TableCell className="text-sm text-muted-foreground">
                              {formatDistanceToNow(new Date(user.created_at), {
                                addSuffix: true,
                                locale: it,
                              })}
                            </TableCell>
                            <TableCell>
                              <div className="flex gap-2">
                                {user.status === 'pending' && (
                                  <>
                                    <Button
                                      variant="outline"
                                      size="sm"
                                      onClick={() => handleApprove(user.id)}
                                    >
                                      <CheckCircle2 className="h-4 w-4 mr-1" />
                                      Approva
                                    </Button>
                                    <Button
                                      variant="outline"
                                      size="sm"
                                      onClick={() => handleReject(user.id)}
                                    >
                                      <XCircle className="h-4 w-4 mr-1" />
                                      Rifiuta
                                    </Button>
                                  </>
                                )}
                                {/* Delete button for all users */}
                                <Button
                                  variant="destructive"
                                  size="sm"
                                  onClick={() => handleDeleteClick(user)}
                                  data-testid={`delete-user-${user.id}`}
                                >
                                  <Trash2 className="h-4 w-4 mr-1" />
                                  Elimina
                                </Button>
                              </div>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                )}
              </div>
            </Tabs>
          </CardContent>
        </Card>

        {/* Delete Confirmation Dialog */}
        <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
          <DialogContent data-testid="delete-user-dialog">
            <DialogHeader>
              <DialogTitle>Elimina Utente</DialogTitle>
              <DialogDescription>
                Sei sicuro di voler eliminare questo utente? Questa azione non può essere annullata.
              </DialogDescription>
            </DialogHeader>
            {userToDelete && (
              <div className="py-4">
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                  <p className="text-sm font-medium text-amber-900">
                    ⚠️ Attenzione: Questa azione è permanente
                  </p>
                  <p className="text-sm text-amber-700 mt-2">
                    <strong>Nome:</strong> {userToDelete.full_name}<br />
                    <strong>Email:</strong> {userToDelete.email}<br />
                    <strong>Ruolo:</strong> {ROLE_LABELS[userToDelete.role]}
                  </p>
                </div>
              </div>
            )}
            <DialogFooter>
              <Button 
                variant="outline" 
                onClick={() => {
                  setDeleteDialogOpen(false);
                  setUserToDelete(null);
                }}
                data-testid="delete-user-cancel"
              >
                Annulla
              </Button>
              <Button 
                variant="destructive" 
                onClick={handleDeleteConfirm}
                data-testid="delete-user-confirm"
              >
                <Trash2 className="mr-2 h-4 w-4" />
                Elimina Definitivamente
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </AppLayout>
  );
}
